import React, { useEffect, useState } from 'react';
import { getSubscriptions, getCharges } from '../subscriptions/core/subscriptionService';
import { Subscription, Charge } from '../subscriptions/core/_models';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Modal } from 'react-bootstrap';

const API_URL = process.env.REACT_APP_API_URL_BACKEND || 'http://localhost:3333/api/v1';

export const ClientSubscriptionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'propostas' | 'ativas' | 'faturas'>('propostas');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [charges, setCharges] = useState<Charge[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageFaturas, setPageFaturas] = useState(1);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [negatingSubscriptionId, setNegatingSubscriptionId] = useState<string | null>(null);

  const ITEMS_PER_PAGE = 10;
  const totalPagesFaturas = Math.ceil(charges.length / ITEMS_PER_PAGE);
  const paginatedCharges = charges.slice((pageFaturas - 1) * ITEMS_PER_PAGE, pageFaturas * ITEMS_PER_PAGE);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsData, chargesData] = await Promise.all([
        getSubscriptions(),
        getCharges()
      ]);
      setSubscriptions(subsData);
      setCharges(chargesData);
    } catch (err) {
      console.error('Erro ao carregar dados do cliente:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleNegate = async () => {
    if (negatingSubscriptionId) {
      try {
        await axios.patch(`${API_URL}/subscriptions/${negatingSubscriptionId}/negate`);
        setNegatingSubscriptionId(null);
        loadData();
      } catch (err) {
        console.error('Erro ao recusar proposta:', err);
        alert('Erro ao recusar a proposta. Tente novamente.');
      }
    }
  };

  const propostas = subscriptions.filter(s => s.status === 'PROPOSTO');
  const ativas = subscriptions.filter(s => ['ATIVO', 'PENDENTE', 'SUSPENSO', 'INADIMPLENTE'].includes(s.status));

  return (
    <div className='card'>
      <div className='card-header card-header-stretch'>
        <h3 className='card-title'>Minhas Assinaturas</h3>
        <div className='card-toolbar'>
          <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0'>
            <li className='nav-item'>
              <a
                className={`nav-link ${activeTab === 'propostas' ? 'active' : ''}`}
                onClick={() => setActiveTab('propostas')}
                style={{ cursor: 'pointer' }}
              >
                Propostas Pendentes
                {propostas.length > 0 && (
                  <span className='badge badge-circle badge-light-danger ms-2'>{propostas.length}</span>
                )}
              </a>
            </li>
            <li className='nav-item'>
              <a
                className={`nav-link ${activeTab === 'ativas' ? 'active' : ''}`}
                onClick={() => setActiveTab('ativas')}
                style={{ cursor: 'pointer' }}
              >
                Assinaturas
              </a>
            </li>
            <li className='nav-item'>
              <a
                className={`nav-link ${activeTab === 'faturas' ? 'active' : ''}`}
                onClick={() => setActiveTab('faturas')}
                style={{ cursor: 'pointer' }}
              >
                Faturas
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className='card-body'>
        {loading ? (
          <div className='d-flex justify-content-center'>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Carregando...</span>
            </div>
          </div>
        ) : (
          <div className='tab-content'>
            {activeTab === 'propostas' && (
              <div className='tab-pane fade show active'>
                {propostas.length === 0 ? (
                  <div className='text-center text-muted py-10'>
                    Nenhuma proposta pendente no momento.
                  </div>
                ) : (
                  <div className='row g-5'>
                    {propostas.map((sub) => (
                      <div key={sub.id} className='col-md-6'>
                        <div className='card border border-primary border-dashed p-6'>
                          <div className='d-flex justify-content-between align-items-center mb-5'>
                            <div>
                              <span className='badge badge-light-primary mb-2'>Nova Proposta</span>
                              <div className='fs-4 fw-bolder'>{sub.plan?.name || 'Plano Personalizado'}</div>
                            </div>
                            <div className='fs-2 fw-bolder text-success'>
                              {Number(sub.monthly_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              <span className='fs-7 fw-normal text-muted'> /mês</span>
                            </div>
                          </div>
                          <p className='text-muted'>
                            Esta proposta foi criada para a usina <strong>{sub.plant?.name}</strong>.
                          </p>
                          <div className='d-flex justify-content-end gap-3 mt-5'>
                            <button
                              className='btn btn-light-danger btn-sm'
                              onClick={() => setNegatingSubscriptionId(sub.id)}
                            >
                              Recusar
                            </button>
                            <Link
                              to={`/checkout/${sub.access_link_token}`}
                              className='btn btn-primary btn-sm'
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              Analisar e Aceitar
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'ativas' && (
              <div className='tab-pane fade show active'>
                {ativas.length === 0 ? (
                  <div className='text-center text-muted py-10'>
                    Você não possui assinaturas ativas.
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                      <thead>
                        <tr className='fw-bolder text-muted'>
                          <th>Plano</th>
                          <th>Usina</th>
                          <th>Valor Mensal</th>
                          <th>Status</th>
                          <th>Criada em</th>
                          <th className='text-end'>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ativas.map((sub) => (
                          <tr key={sub.id}>
                            <td>
                              <span className='text-gray-800 fw-bolder fs-6'>{sub.plan?.name || 'Personalizado'}</span>
                            </td>
                            <td>{sub.plant?.name || '-'}</td>
                            <td>
                              {Number(sub.monthly_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </td>
                            <td>
                              <span className={`badge badge-light-${sub.status === 'ATIVO' ? 'success' : sub.status === 'SUSPENSO' || sub.status === 'INADIMPLENTE' ? 'danger' : 'warning'}`}>
                                {sub.status}
                              </span>
                            </td>
                            <td>{new Date(sub.created_at).toLocaleDateString('pt-BR')}</td>
                            <td className='text-end'>
                              {sub.status === 'PENDENTE' && (() => {
                                const pendingCharge = charges.find(c => (c.subscription_id === sub.id || (c as any).subscription?.id === sub.id) && c.status === 'PENDENTE');
                                if (pendingCharge && pendingCharge.invoice_link) {
                                  return (
                                    <button
                                      onClick={() => window.open(pendingCharge.invoice_link, '_blank')}
                                      className='btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1'
                                      title='Efetuar Pagamento'
                                    >
                                      <i className='bi bi-credit-card fs-3'></i>
                                    </button>
                                  );
                                }
                                return null;
                              })()}
                              <button
                                onClick={() => setSelectedSubscription(sub)}
                                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                title='Ver Detalhes'
                              >
                                <i className='bi bi-eye fs-3'></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'faturas' && (
              <div className='tab-pane fade show active'>
                {charges.length === 0 ? (
                  <div className='text-center text-muted py-10'>
                    Nenhuma fatura encontrada.
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                      <thead>
                        <tr className='fw-bolder text-muted'>
                          <th>Vencimento</th>
                          <th>Valor</th>
                          <th>Método</th>
                          <th>Status</th>
                          <th>Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCharges.map((charge) => (
                          <tr key={charge.id}>
                            <td>{new Date(charge.due_date).toLocaleDateString('pt-BR')}</td>
                            <td>
                              {Number(charge.gross_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </td>
                            <td>{charge.payment_method}</td>
                            <td>
                              <span className={`badge badge-light-${charge.status === 'PAGA' ? 'success' : charge.status === 'VENCIDA' ? 'danger' : charge.status === 'PENDENTE' ? 'warning' : 'secondary'}`}>
                                {charge.status}
                              </span>
                            </td>
                            <td>
                              <div className='d-flex gap-2'>
                                {charge.invoice_link && (
                                  <a
                                    href={charge.invoice_link}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='btn btn-sm btn-light btn-active-light-primary'
                                  >
                                    Ver Fatura
                                  </a>
                                )}
                                {charge.invoice_link && (
                                  <a
                                    href={charge.invoice_link}
                                    target='_blank'
                                    rel='noreferrer'
                                    download={`Fatura-${charge.id}.pdf`}
                                    className='btn btn-sm btn-primary'
                                    title='Baixar PDF'
                                  >
                                    <i className='bi bi-download'></i> Download
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {totalPagesFaturas > 1 && (
                      <div className='d-flex justify-content-between align-items-center mt-5'>
                        <div className='text-muted fs-6'>
                          Mostrando {(pageFaturas - 1) * ITEMS_PER_PAGE + 1} a {Math.min(pageFaturas * ITEMS_PER_PAGE, charges.length)} de {charges.length} faturas
                        </div>
                        <ul className='pagination'>
                          <li className={`page-item previous ${pageFaturas === 1 ? 'disabled' : ''}`}>
                            <button className='page-link' onClick={() => setPageFaturas(pageFaturas - 1)}>
                              <i className='previous'></i>
                            </button>
                          </li>
                          {[...Array(totalPagesFaturas)].map((_, i) => (
                            <li key={i} className={`page-item ${pageFaturas === i + 1 ? 'active' : ''}`}>
                              <button className='page-link' onClick={() => setPageFaturas(i + 1)}>{i + 1}</button>
                            </li>
                          ))}
                          <li className={`page-item next ${pageFaturas === totalPagesFaturas ? 'disabled' : ''}`}>
                            <button className='page-link' onClick={() => setPageFaturas(pageFaturas + 1)}>
                              <i className='next'></i>
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal show={!!selectedSubscription} onHide={() => setSelectedSubscription(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Assinatura</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubscription && (
            <>
              <div className='row mb-8'>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted'>Plano</div>
                  <div className='fw-bolder fs-5 text-gray-800'>{selectedSubscription.plan?.name || 'Personalizado'}</div>
                </div>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted'>Usina</div>
                  <div className='fw-bolder fs-5 text-gray-800'>{selectedSubscription.plant?.name || '-'}</div>
                </div>
                <div className='col-md-4'>
                  <div className='fw-bold text-muted'>Valor Mensal</div>
                  <div className='fw-bolder fs-5 text-success'>{Number(selectedSubscription.monthly_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                </div>
              </div>

              <h5 className='mb-4'>Itens Inclusos no Plano</h5>
              {selectedSubscription.subscriptionItems && selectedSubscription.subscriptionItems.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed fs-6 gy-3'>
                    <thead>
                      <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0 bg-light'>
                        <th className='ps-4 rounded-start'>Item / Produto</th>
                        <th>Qtd</th>
                        <th className='text-end pe-4 rounded-end'>Total</th>
                      </tr>
                    </thead>
                    <tbody className='text-gray-600 fw-bold'>
                      {selectedSubscription.subscriptionItems.map(item => (
                        <tr key={item.id}>
                          <td className='ps-4'>
                            {item.product?.name || item.typeService?.name || 'Item de Assinatura'}
                            {item.is_included && <span className='badge badge-light-success ms-2'>Incluso</span>}
                          </td>
                          <td>{item.quantity}</td>
                          <td className='text-end pe-4'>{Number(item.total_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='alert alert-light-warning'>
                  Nenhum item detalhado encontrado para esta assinatura.
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!negatingSubscriptionId} onHide={() => setNegatingSubscriptionId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Recusar Proposta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja recusar esta proposta? Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-light' onClick={() => setNegatingSubscriptionId(null)}>
            Cancelar
          </button>
          <button className='btn btn-danger' onClick={handleNegate}>
            Confirmar Recusa
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
